import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Shield, AlertCircle } from 'lucide-react';

interface ConsentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: () => void;
  professionalName: string;
}

const ConsentDialog: React.FC<ConsentDialogProps> = ({
  isOpen,
  onOpenChange,
  onApprove,
  professionalName
}) => {
  const { t } = useTranslation();
  const { direction } = useDirection();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir={direction}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            {t('dialogs.consent.title')}
          </DialogTitle>
          <DialogDescription>
            {t('dialogs.consent.description', { professionalName })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-2">{t('dialogs.consent.importantTitle')}</p>
                <p>
                  {t('dialogs.consent.importantMessage')}
                </p>
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('dialogs.consent.afterApproval', { professionalName })}
          </p>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onApprove}
            className="w-full sm:w-auto bg-[#00D09E] hover:bg-[#00C090]"
          >
            {t('dialogs.consent.approveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentDialog;